import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import { getYearRenderMode } from '../../common/utils/getYearRenderMode';
import ensureCommunityRewind from '../utils/ensureCommunityRewind';
export default class ForumRewindPage extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "loadingContent", false);
    _defineProperty(this, "generating", false);
    _defineProperty(this, "communitySnapshot", null);
    _defineProperty(this, "userHasSnapshot", false);
    _defineProperty(this, "selectedYear", 0);
    _defineProperty(this, "availableYears", []);
    _defineProperty(this, "preparingCommunity", false);
    _defineProperty(this, "isDestroyed", false);
  }
  onremove(vnode) {
    this.isDestroyed = true;
    super.onremove(vnode);
  }
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(app.translator.trans('huseyinfiliz-rewind.forum.page.title'));
    this.selectedYear = app.forum.attribute('rewindActiveYear');
    const canView = app.forum.attribute('canViewRewind');
    const canModerate = app.forum.attribute('canModerateRewind');
    const enabled = app.forum.attribute('rewindEnabled');
    if (enabled && canView || canModerate) {
      this.loadAvailableYears().then(() => this.loadData(true));
    } else {
      this.loading = false;
    }
  }
  async loadAvailableYears() {
    try {
      const response = await app.store.find('rw-community', {
        sort: '-year',
        page: {
          limit: 20
        }
      });
      const results = Array.isArray(response) ? response : [response];
      const years = results.filter(s => s && s.year).map(s => s.year()).sort((a, b) => b - a);
      this.availableYears = [...new Set(years)];
      if (!this.availableYears.includes(this.selectedYear)) {
        this.availableYears.unshift(this.selectedYear);
      }
    } catch {
      this.availableYears = [this.selectedYear];
    }
  }
  async loadData(initial) {
    if (initial === void 0) {
      initial = false;
    }
    if (initial) {
      this.loading = true;
    } else {
      this.loadingContent = true;
    }
    m.redraw();
    const year = this.selectedYear;
    try {
      const response = await app.store.find('rw-community', {
        filter: {
          year
        }
      });
      const results = Array.isArray(response) ? response : [response];
      this.communitySnapshot = results.length > 0 ? results[0] : null;
      if (!this.communitySnapshot) {
        this.preparingCommunity = true;
        m.redraw();
        if (await ensureCommunityRewind(year, () => this.isDestroyed || this.selectedYear !== year)) {
          const ready = await app.store.find('rw-community', {
            filter: {
              year
            }
          });
          this.communitySnapshot = (Array.isArray(ready) ? ready[0] : ready) || null;
        }
      }
    } catch {
      this.communitySnapshot = null;
    } finally {
      this.preparingCommunity = false;
    }
    const user = app.session.user;
    if (user) {
      try {
        const userResponse = await app.store.find('rw-snaps', {
          filter: {
            user: user.id(),
            year
          }
        });
        const userResults = Array.isArray(userResponse) ? userResponse : [userResponse];
        this.userHasSnapshot = userResults.length > 0 && userResults[0] !== null;
      } catch {
        this.userHasSnapshot = false;
      }
    }
    this.loading = false;
    this.loadingContent = false;
    m.redraw();
  }
  switchYear(year) {
    if (year === this.selectedYear) return;
    this.selectedYear = year;
    this.loadData();
  }
  confirmRegenerate() {
    if (confirm(app.translator.trans('huseyinfiliz-rewind.forum.community.regenerate_confirm'))) {
      this.generateCommunity();
    }
  }
  async generateCommunity() {
    this.generating = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/rw-community/generate-steps',
        body: {
          year: this.selectedYear
        }
      });
      for (const step of response.steps) {
        let complete = false;
        while (!complete && !this.isDestroyed) {
          const progress = await app.request({
            method: 'POST',
            url: app.forum.attribute('apiUrl') + '/rw-community/generate-step',
            body: {
              year: this.selectedYear,
              step
            }
          });
          complete = progress.data.stepComplete;
        }
      }
      await this.loadData();
    } catch (e) {
      const msg = e?.responseJSON?.errors?.[0]?.detail || e?.message || 'Failed to generate community rewind';
      app.alerts.show({
        type: 'error'
      }, msg);
    }
    this.generating = false;
    m.redraw();
  }
  view() {
    const year = this.selectedYear;
    const user = app.session.user;
    const canView = app.forum.attribute('canViewRewind');
    const canModerate = app.forum.attribute('canModerateRewind');
    const enabled = app.forum.attribute('rewindEnabled');
    if (!(enabled && canView) && !canModerate) {
      return m("div", {
        className: "RewindPage"
      }, m("div", {
        className: "container"
      }, m("div", {
        className: "RewindPage-emptyState"
      }, m("div", {
        className: "RewindPage-emptyStateIcon"
      }, m("i", {
        className: "fas fa-lock"
      })), m("h3", null, app.translator.trans('huseyinfiliz-rewind.forum.page.not_available')))));
    }
    return m(PageStructure, {
      className: "RewindPage",
      hero: () => this.renderHero(enabled, canModerate),
      sidebar: () => m(IndexSidebar, null)
    }, m("div", {
      className: "RewindPage-content"
    }, this.renderToolbar(year, user), this.loading || this.loadingContent ? m("div", {
      className: "RewindPage-contentLoading"
    }, m(LoadingIndicator, null), this.preparingCommunity && m("p", null, app.translator.trans('huseyinfiliz-rewind.forum.community.preparing'))) : this.communitySnapshot ? this.renderCommunityStats() : this.renderNoCommunity()));
  }
  renderHero(enabled, canModerate) {
    return m('[', null, !enabled && canModerate && m("div", {
      className: "RewindPage-adminNotice"
    }, m("div", {
      className: "container"
    }, m("i", {
      className: "fas fa-eye-slash"
    }), " ", app.translator.trans('huseyinfiliz-rewind.forum.page.admin_notice'))), m("header", {
      className: "Hero RewindHero"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "containerNarrow"
    }, m("h1", {
      className: "Hero-title"
    }, m("i", {
      className: "fas fa-history"
    }), " ", app.translator.trans('huseyinfiliz-rewind.forum.page.hero_title', {
      year: this.selectedYear
    })), m("div", {
      className: "Hero-subtitle"
    }, app.translator.trans('huseyinfiliz-rewind.forum.page.hero_subtitle'))))));
  }
  renderToolbar(year, user) {
    const showYearSelector = this.availableYears.length > 1;
    const profileUrl = user ? app.route('huseyinfiliz-rewind.profile', {
      username: user.slug()
    }) : null;
    const isAdmin = app.session.user?.isAdmin();
    return m("div", {
      className: "RewindPage-toolbar"
    }, showYearSelector ? m("div", {
      className: "RewindPage-yearSelector"
    }, this.availableYears.map(y => m("button", {
      key: y,
      className: 'RewindPage-yearBtn' + (y === this.selectedYear ? ' active' : ''),
      onclick: () => this.switchYear(y),
      disabled: this.loading || this.loadingContent || this.generating
    }, y))) : m("div", null), m("div", {
      className: "RewindPage-toolbarRight"
    }, isAdmin && this.communitySnapshot && m("button", {
      className: "Button RewindPage-myRewindBtn",
      onclick: () => this.confirmRegenerate(),
      disabled: this.generating
    }, m("i", {
      className: this.generating ? 'fas fa-spinner fa-spin' : 'fas fa-sync'
    }), m("span", null, app.translator.trans('huseyinfiliz-rewind.forum.community.regenerate_short'))), user && profileUrl && m(Link, {
      className: "Button RewindPage-myRewindBtn",
      href: profileUrl
    }, m("i", {
      className: "fas fa-user"
    }), m("span", null, app.translator.trans('huseyinfiliz-rewind.forum.page.my_rewind')))));
  }
  renderNoCommunity() {
    const isAdmin = app.session.user?.isAdmin();
    return m("div", {
      className: "RewindPage-emptyState"
    }, m("div", {
      className: "RewindPage-emptyStateIcon"
    }, m("i", {
      className: "fas fa-chart-bar"
    })), m("h3", null, app.translator.trans('huseyinfiliz-rewind.forum.community.title')), isAdmin ? m("div", null, m("p", null, app.translator.trans('huseyinfiliz-rewind.forum.community.not_generated')), m(Button, {
      className: "Button Button--primary Button--block RewindPage-generateBtn",
      loading: this.generating,
      onclick: () => this.generateCommunity(),
      icon: "fas fa-magic"
    }, app.translator.trans('huseyinfiliz-rewind.forum.community.generate_button'))) : m("p", {
      className: "RewindPage-comingSoon"
    }, app.translator.trans('huseyinfiliz-rewind.forum.community.coming_soon')));
  }
  renderCommunityStats() {
    const data = this.communitySnapshot.snapshotData();
    if (!data) return null;
    const isAdmin = app.session.user?.isAdmin();

    // Check if community has any meaningful data
    const hasContent = !!(data.total_posts?.count > 0 || data.total_discussions?.count > 0 || data.new_users?.count > 0 || data.top_tag?.name || data.top_discussion?.id || data.most_active_user?.username);
    return m("div", {
      className: "RewindPage-community"
    }, hasContent ? m("div", {
      className: "RewindPage-slideshowBanner",
      onclick: () => {
        const renderMode = getYearRenderMode(this.selectedYear);
        if (renderMode === 'blade') {
          const baseUrl = app.forum.attribute('baseUrl') || '';
          window.location.href = `${baseUrl}/rewind/view/${this.selectedYear}`;
        } else {
          m.route.set(app.route('huseyinfiliz-rewind.community-slideshow', {
            year: this.selectedYear
          }));
        }
      }
    }, m("div", {
      className: "RewindPage-slideshowBannerBg"
    }), m("div", {
      className: "RewindPage-slideshowBannerContent"
    }, m("i", {
      className: "fas fa-play-circle"
    }), m("div", null, m("div", {
      className: "RewindPage-slideshowBannerTitle"
    }, app.translator.trans('huseyinfiliz-rewind.forum.community.watch_slideshow', {
      year: this.selectedYear
    })), m("div", {
      className: "RewindPage-slideshowBannerSub"
    }, app.translator.trans('huseyinfiliz-rewind.forum.community.watch_slideshow_desc'))))) : m("div", {
      className: "RewindPage-emptyBanner"
    }, m("i", {
      className: "fas fa-ghost"
    }), m("span", null, app.translator.trans('huseyinfiliz-rewind.forum.community.no_activity'))), hasContent && m("div", {
      className: "RewindPage-sectionHeader"
    }, m("h3", null, app.translator.trans('huseyinfiliz-rewind.forum.community.title'))), m("div", {
      className: "RewindPage-statsGrid"
    }, data.new_users?.count > 0 && this.renderStatCard('fas fa-user-plus', data.new_users.count, app.translator.trans('huseyinfiliz-rewind.forum.community.new_users')), data.total_posts?.count > 0 && this.renderStatCard('fas fa-comment', data.total_posts.count, app.translator.trans('huseyinfiliz-rewind.forum.community.total_posts')), data.total_discussions?.count > 0 && this.renderStatCard('fas fa-comments', data.total_discussions.count, app.translator.trans('huseyinfiliz-rewind.forum.community.total_discussions')), data.top_tag?.name && this.renderStatCard('fas fa-tag', data.top_tag.name, app.translator.trans('huseyinfiliz-rewind.forum.community.top_tag'), true), data.top_discussion?.id && this.renderWideCard('fas fa-fire', app.translator.trans('huseyinfiliz-rewind.forum.community.top_discussion'), data.top_discussion.title, data.top_discussion.post_count ? app.translator.trans('huseyinfiliz-rewind.forum.community.post_count_label', {
      count: data.top_discussion.post_count
    }) : undefined), data.most_active_user?.username && this.renderWideCard('fas fa-trophy', app.translator.trans('huseyinfiliz-rewind.forum.community.most_active_user'), data.most_active_user.username, data.most_active_user.post_count ? app.translator.trans('huseyinfiliz-rewind.forum.community.post_count_label', {
      count: data.most_active_user.post_count
    }) : undefined)));
  }
  renderStatCard(icon, value, label, isText) {
    if (isText === void 0) {
      isText = false;
    }
    return m("div", {
      className: "RewindPage-statCard"
    }, m("div", {
      className: "RewindPage-statCardIcon"
    }, m("i", {
      className: icon
    })), m("div", {
      className: 'RewindPage-statCardValue' + (isText ? ' RewindPage-statCardValue--text' : '')
    }, typeof value === 'number' ? value.toLocaleString() : value), m("div", {
      className: "RewindPage-statCardLabel"
    }, label));
  }
  renderWideCard(icon, label, title, subtitle) {
    return m("div", {
      className: "RewindPage-statCard RewindPage-statCard--wide"
    }, m("div", {
      className: "RewindPage-statCardIcon"
    }, m("i", {
      className: icon
    })), m("div", {
      className: "RewindPage-statCardLabel"
    }, label), m("div", {
      className: "RewindPage-statCardValue RewindPage-statCardValue--text"
    }, title), subtitle && m("div", {
      className: "RewindPage-statCardSubtitle"
    }, subtitle));
  }
}
flarum.reg.add('huseyinfiliz-rewind', 'forum/components/ForumRewindPage', ForumRewindPage);